import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategoriesSection() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<typeof categories[0] | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat);
    setDropdownOpen(false);
    window.open(cat.url, "_blank");
  };

  return (
    <section id="categories" className="relative py-16 md:py-24 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
            {t("categories.title")}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            {t("categories.subtitle")}
          </p>
        </div>

        <div className="max-w-md mx-auto mb-10" ref={dropdownRef}>
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between bg-background-100 border border-background-300/60 rounded-lg px-5 py-3.5 text-sm text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className={selectedCategory ? "text-foreground-900" : "text-foreground-500"}>
                {selectedCategory ? selectedCategory.namePl : t("categories.dropdown.placeholder")}
              </span>
              <div className="w-5 h-5 flex items-center justify-center">
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-foreground-500"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-foreground-500"></i>
                )}
              </div>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-40 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleSelect(cat)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors text-left whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-base text-primary-500`}></i>
                    </div>
                    <span>{cat.namePl}</span>
                    <span className="text-xs text-foreground-400 ml-auto">{cat.nameEn}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 hover:bg-background-50 transition-all duration-200 cursor-pointer"
            >
              <div className="aspect-square overflow-hidden bg-background-200/50">
                <img
                  src={cat.imageUrl}
                  alt={cat.namePl}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex items-center gap-2 px-3 py-2.5">
                <div className="w-6 h-6 rounded-md bg-primary-50 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 transition-colors">
                  <i className={`${cat.icon} text-xs text-primary-500 group-hover:text-primary-600`}></i>
                </div>
                <span className="text-xs md:text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                  {cat.namePl}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}